/**
 * Sugestão gratuita de duração.
 *
 * Quando o utilizador nomeia um destino e pergunta "quantos dias?" (ou
 * equivalente noutro idioma) sem indicar duração, não pedimos um número vazio:
 * propomos 2/3/4 dias com uma descrição do que cabe em cada opção. É totalmente
 * local — não consome IA, Maps nem quota.
 */

export type DurationSuggestionOption = {
  days: number;
  /** Título curto: "Essenciais", "Experiência completa", "Ritmo tranquilo". */
  title: string;
  /** O que cabe nesses dias, já com referências do destino quando existem. */
  detail: string;
};

export type DurationSuggestion = {
  city: string;
  headline: string;
  options: DurationSuggestionOption[];
};

type Lang = "pt" | "en" | "es" | "fr" | "it" | "de" | "ja";

const TITLES: Record<Lang, [string, string, string]> = {
  pt: ["Essenciais", "Experiência completa", "Ritmo tranquilo"],
  en: ["Essentials", "Full experience", "Relaxed pace"],
  es: ["Esenciales", "Experiencia completa", "Ritmo tranquilo"],
  fr: ["Essentiel", "Expérience complète", "Rythme tranquille"],
  it: ["Essenziali", "Esperienza completa", "Ritmo tranquillo"],
  de: ["Highlights", "Komplettes Erlebnis", "Entspanntes Tempo"],
  ja: ["定番", "しっかり満喫", "ゆったり"],
};

const GENERIC: Record<Lang, [string, string, string]> = {
  pt: [
    "os clássicos e o centro histórico.",
    "os clássicos mais bairros, museus ou um passeio nos arredores.",
    "bairros, museus e arredores sem pressa.",
  ],
  en: [
    "the classics and the historic centre.",
    "the classics plus neighbourhoods, museums or a nearby day trip.",
    "neighbourhoods, museums and surroundings without rushing.",
  ],
  es: [
    "los clásicos y el centro histórico.",
    "los clásicos más barrios, museos o una escapada cercana.",
    "barrios, museos y alrededores sin prisas.",
  ],
  fr: [
    "les classiques et le centre historique.",
    "les classiques plus quartiers, musées ou une escapade proche.",
    "quartiers, musées et alentours sans se presser.",
  ],
  it: [
    "i classici e il centro storico.",
    "i classici più quartieri, musei o una gita vicina.",
    "quartieri, musei e dintorni con calma.",
  ],
  de: [
    "die Klassiker und die Altstadt.",
    "die Klassiker plus Viertel, Museen oder ein Ausflug in die Umgebung.",
    "Viertel, Museen und Umgebung ohne Hetze.",
  ],
  ja: ["定番と旧市街。", "定番に加えて街歩き・美術館・近郊の小旅行。", "街歩きや美術館、近郊をゆっくり。"],
};

/** Referências concretas para destinos onde a recomendação é conhecida. */
const CITY_HINTS: Record<string, Partial<Record<Lang, [string, string, string]>>> = {
  venice: {
    pt: [
      "São Marcos, Rialto e os canais principais.",
      "clássicos de Veneza + Murano/Burano ou mais museus.",
      "bairros, museus e ilhas sem pressa.",
    ],
    en: [
      "St Mark's, Rialto and the main canals.",
      "Venice classics + Murano/Burano or more museums.",
      "neighbourhoods, museums and islands without rushing.",
    ],
    it: [
      "San Marco, Rialto e i canali principali.",
      "i classici di Venezia + Murano/Burano o più musei.",
      "sestieri, musei e isole con calma.",
    ],
  },
};

const HEADLINE: Record<Lang, (city: string, range: string) => string> = {
  pt: (c, r) => `Para uma primeira visita a ${c}, recomendo ${r} dias.`,
  en: (c, r) => `For a first visit to ${c}, I recommend ${r} days.`,
  es: (c, r) => `Para una primera visita a ${c}, recomiendo ${r} días.`,
  fr: (c, r) => `Pour une première visite à ${c}, je recommande ${r} jours.`,
  it: (c, r) => `Per una prima visita a ${c}, consiglio ${r} giorni.`,
  de: (c, r) => `Für einen ersten Besuch in ${c} empfehle ich ${r} Tage.`,
  ja: (c, r) => `${c}の初訪問なら${r}日がおすすめです。`,
};

const DAY_WORD: Record<Lang, string> = {
  pt: "dias",
  en: "days",
  es: "días",
  fr: "jours",
  it: "giorni",
  de: "Tage",
  ja: "日",
};

const ASKS_HOW_LONG =
  /\b(quantos?\s+dias|quanto\s+tempo|quantas?\s+noites|how\s+many\s+days|how\s+long|cu[aá]ntos?\s+d[ií]as|combien\s+de\s+jours|quanti\s+giorni|wie\s+viele\s+tage|何日)/iu;

const canon = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const ALIASES: Record<string, string> = {
  veneza: "venice",
  venezia: "venice",
  venise: "venice",
  venice: "venice",
  venedig: "venice",
};

/** O texto pergunta explicitamente pela duração (sem a indicar)? */
export function asksForDurationAdvice(prompt: string): boolean {
  return ASKS_HOW_LONG.test(prompt ?? "");
}

const lang = (value: string): Lang =>
  (["pt", "en", "es", "fr", "it", "de", "ja"] as const).includes(value as Lang) ? (value as Lang) : "en";

/**
 * Recomendação gratuita de 2/3/4 dias para o destino já resolvido.
 * `city` deve ser o nome canónico/apresentável ("Veneza, Itália" ou "Veneza").
 */
export function suggestTripDuration(city: string, language = "pt"): DurationSuggestion | null {
  const name = (city ?? "").replace(/\s+/gu, " ").trim();
  if (!name) return null;
  const l = lang(language);
  const key = ALIASES[canon(name.split(",")[0] ?? "")] ?? canon(name.split(",")[0] ?? "");
  const details = CITY_HINTS[key]?.[l] ?? CITY_HINTS[key]?.en ?? GENERIC[l];
  const titles = TITLES[l];
  const word = DAY_WORD[l];
  return {
    city: name,
    headline: HEADLINE[l](name, "2–3"),
    options: [2, 3, 4].map((days, i) => ({
      days,
      title: titles[i]!,
      detail: `${days} ${word} — ${titles[i]}: ${details[i]}`,
    })),
  };
}
